import React, { useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import useSWR from 'swr';
import { ChevronDown, ChevronRight, ChevronUp, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useAppState } from '../app/appContext';
import { BranchRequiredState } from '@/components/common/BranchRequiredState';
import { useT } from '@/contexts/I18nContext';
import { useCan, useCanAnyZone } from '@/hooks/useCan';
import {
  attachVisitTemplateBranchGroup,
  attachVisitTemplateZone,
  createVisitGroup,
  createVisitParameter,
  createVisitTemplate,
  deleteVisitGroup,
  deleteVisitParameter,
  deleteVisitTemplate,
  detachVisitTemplateZone,
  getVisitTemplate,
  getVisitsZoneSettings,
  listVisitTemplates,
  patchVisitGroup,
  patchVisitParameter,
  patchVisitsZoneChat,
  patchVisitsZoneSettings,
  type VisitParameterDto,
  type VisitParameterGroupDto,
  type VisitTemplateSummaryDto,
} from '../api/visits';
import { listBranchGroups, type BranchGroupSummary } from '@/api/adminGroups';
import { Select } from '@/components/ui/select';
import { extractApiError } from '@/utils/apiError';
import { useCurrentUser } from '@/hooks/useCurrentUser';

// VISITS-2 (#175): Настройки → Визиты. Feature flag (per-zone, off by
// default) + template editor (groups → parameters, weight да/нет, formula
// coefficient). Fields are plain always-active inputs in edit mode — no
// "select the cell, then type" — per Alexey's explicit ask. Weights and
// coefficients are never clamped: negative values are a supported case
// (e.g. "smoking in the room" = weight_yes -10), so this UI must not round
// them toward zero or reject the minus sign.

export default function VisitsSettings() {
  const { zone } = useAppState();
  const zoneId = zone.selectedZoneId;
  if (!zoneId) return <BranchRequiredState />;
  return (
    <div className="space-y-4">
      <FeatureFlagCard zoneId={zoneId} />
      <TemplatesCard zoneId={zoneId} />
    </div>
  );
}

// ── Feature flag ─────────────────────────────────────────────────────────

function FeatureFlagCard({ zoneId }: { zoneId: string }) {
  const { t } = useT();
  const canEdit = useCan('zone.config.edit', zoneId);
  const { data, isLoading, mutate } = useSWR(`visits-zone-settings:${zoneId}`, () =>
    getVisitsZoneSettings(zoneId).then(r => r.data),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleToggle(next: boolean) {
    setError(null);
    setBusy(true);
    try {
      await patchVisitsZoneSettings(zoneId, next);
      mutate();
    } catch (e) {
      setError(extractApiError(e, t('error.general')));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card variant="section">
      <CardHeader>
        <CardTitle>{t('settings.visits')}</CardTitle>
        <CardDescription>{t('visits.featureDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label htmlFor="visits-enabled" className="text-sm font-medium">{t('visits.enableLabel')}</Label>
            <p className="text-xs text-muted-foreground">{t('visits.enableHint')}</p>
          </div>
          <div className="flex items-center gap-2">
            {(isLoading || busy) && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
            <Switch
              id="visits-enabled"
              checked={!!data?.enabled}
              disabled={!canEdit || isLoading || busy}
              onCheckedChange={handleToggle}
            />
          </div>
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
        {!isLoading && data && <VisitsChatSettings zoneId={zoneId} data={data} canEdit={canEdit} onSaved={mutate} />}
      </CardContent>
    </Card>
  );
}

// "Отправить команде" (Alexey 2026-08-27): dedicated chat/topic override for
// visit notifications, same pattern + same collapsed-by-default UI as
// ChecklistsSettings' "Telegram-чат (ID и тема)" block — operators only
// touch this at onboarding, so it stays out of the way otherwise. Empty
// fields fall back to the zone's default client chat (see #288 — that
// default-chat model is meant to be consistent across every category).
function VisitsChatSettings({
  zoneId, data, canEdit, onSaved,
}: {
  zoneId: string;
  data: { visits_chat_id: string | null; visits_thread_id: string | null };
  canEdit: boolean;
  onSaved: () => void;
}) {
  const { t } = useT();
  const [expanded, setExpanded] = useState(false);
  const [chatId, setChatId] = useState(data.visits_chat_id ?? '');
  const [threadId, setThreadId] = useState(data.visits_thread_id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function commit() {
    const nextChatId = chatId.trim() || null;
    const nextThreadId = threadId.trim() || null;
    if (nextChatId === data.visits_chat_id && nextThreadId === data.visits_thread_id) return;
    setBusy(true);
    setError(null);
    try {
      await patchVisitsZoneChat(zoneId, nextChatId, nextThreadId);
      onSaved();
    } catch (e) {
      setError(extractApiError(e, t('error.general')));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-md border border-border">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="flex w-full items-center justify-between px-3 py-2 text-xs text-muted-foreground transition-colors hover:bg-muted/40"
      >
        <span>{t('visits.chatSectionTitle')}</span>
        {expanded ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
      </button>
      {expanded && (
        <div className="grid gap-3 border-t border-border p-3 sm:grid-cols-2">
          <div className="grid gap-1">
            <Label className="text-xs">{t('visits.chatIdLabel')}</Label>
            <Input
              className="font-mono text-sm"
              value={chatId}
              onChange={e => setChatId(e.target.value)}
              onBlur={commit}
              disabled={!canEdit || busy}
              placeholder="-100123456789"
            />
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">{t('visits.chatThreadLabel')}</Label>
            <Input
              className="font-mono text-sm"
              value={threadId}
              onChange={e => setThreadId(e.target.value)}
              onBlur={commit}
              disabled={!canEdit || busy}
              placeholder="1234"
            />
          </div>
          {error && <p className="text-xs text-destructive sm:col-span-2">{error}</p>}
          <p className="text-xs text-muted-foreground sm:col-span-2">{t('visits.chatSectionHint')}</p>
        </div>
      )}
    </div>
  );
}

// ── Templates list ───────────────────────────────────────────────────────

function TemplatesCard({ zoneId }: { zoneId: string }) {
  const { t } = useT();
  const { user } = useCurrentUser();
  const canCreateTemplate = useCanAnyZone('zone.config.edit');
  const { data, error, isLoading, mutate } = useSWR('visits-templates', () =>
    listVisitTemplates().then(r => r.data.results),
  );
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  async function handleCreate() {
    const name = window.prompt(t('visits.newTemplatePrompt'));
    if (!name?.trim()) return;
    setCreating(true);
    try {
      const resp = await createVisitTemplate(name.trim());
      mutate();
      setExpanded(resp.data.id);
    } catch (e) {
      window.alert(extractApiError(e, t('error.general')));
    } finally {
      setCreating(false);
    }
  }

  return (
    <Card variant="section">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <div>
          <CardTitle>{t('visits.templatesTitle')}</CardTitle>
          <CardDescription>{t('visits.templatesDescription')}</CardDescription>
        </div>
        {canCreateTemplate && (
          <Button type="button" size="sm" className="gap-1.5" disabled={creating} onClick={handleCreate}>
            <Plus className="size-3.5" /> {t('visits.newTemplate')}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {error && !data && <ServerStatusNotice variant="card" error={error} onRetry={() => mutate()} />}
        {isLoading && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
          </p>
        )}
        {!isLoading && (data ?? []).length === 0 && (
          <p className="text-sm text-muted-foreground">{t('visits.noTemplates')}</p>
        )}
        {(data ?? []).map((tpl: VisitTemplateSummaryDto) => (
          <TemplateRow
            key={tpl.id}
            summary={tpl}
            zoneId={zoneId}
            expanded={expanded === tpl.id}
            onToggle={() => setExpanded(expanded === tpl.id ? null : tpl.id)}
            onListChanged={mutate}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function TemplateRow({
  summary, zoneId, expanded, onToggle, onListChanged,
}: {
  summary: VisitTemplateSummaryDto;
  zoneId: string;
  expanded: boolean;
  onToggle: () => void;
  onListChanged: () => void;
}) {
  const { t } = useT();
  const { user } = useCurrentUser();
  // Gate for mutating this template (attach/detach-here, edit groups/params,
  // delete). Backend allows it via configure_visits on ANY zone the template
  // is attached to, but this UI only knows the currently-selected zone — so
  // this is a safe under-approximation (a manager via a different attached
  // branch would need to switch to it here), never an over-approximation.
  // Was previously not checked at all client-side: every viewer with any
  // view_visits access saw live Delete/Edit buttons on every shared template
  // (Alexey 09.09 — "все подряд... могут удалить шаблон, копировать его").
  const canManageHere = useCan('zone.config.edit', zoneId) || !!user?.is_superuser;
  const swrKey = expanded ? `visits-template:${summary.id}` : null;
  const { data: detail, mutate } = useSWR(swrKey, () => getVisitTemplate(summary.id).then(r => r.data));
  const [editMode, setEditMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const branchGroupsKey = expanded && user?.is_superuser ? 'visits-branch-groups' : null;
  const { data: branchGroups } = useSWR(branchGroupsKey, () => listBranchGroups().then(r => r.data.results));
  const [selectedBranchGroup, setSelectedBranchGroup] = useState('');

  const attachedHere = !!detail?.zones.some(z => String(z.id) === String(zoneId));

  async function handleAttachToggle() {
    setErr(null);
    setBusy(true);
    try {
      if (attachedHere) {
        await detachVisitTemplateZone(summary.id, Number(zoneId));
      } else {
        await attachVisitTemplateZone(summary.id, Number(zoneId));
      }
      mutate();
      onListChanged();
    } catch (e) {
      setErr(extractApiError(e, t('error.general')));
    } finally {
      setBusy(false);
    }
  }

  async function handleAttachBranchGroup() {
    if (!selectedBranchGroup) return;
    setErr(null);
    setBusy(true);
    try {
      await attachVisitTemplateBranchGroup(summary.id, Number(selectedBranchGroup));
      mutate();
      onListChanged();
    } catch (e) {
      setErr(extractApiError(e, t('error.general')));
    } finally {
      setBusy(false);
    }
  }

  async function handleDetachZone(zid: number) {
    setErr(null);
    setBusy(true);
    try {
      await detachVisitTemplateZone(summary.id, zid);
      mutate();
      onListChanged();
    } catch (e) {
      setErr(extractApiError(e, t('error.general')));
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteTemplate() {
    if (!window.confirm(t('visits.deleteTemplateConfirm', { name: summary.name }))) return;
    setBusy(true);
    try {
      await deleteVisitTemplate(summary.id);
      onListChanged();
    } catch (e) {
      setErr(extractApiError(e, t('error.general')));
      setBusy(false);
    }
  }

  return (
    <div className="rounded-md border">
      <div className="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm">
        <button type="button" onClick={onToggle} className="flex min-w-0 flex-1 items-center gap-1.5 text-left font-medium hover:underline">
          {expanded ? <ChevronDown className="size-3.5 shrink-0" /> : <ChevronRight className="size-3.5 shrink-0" />}
          <span className="truncate">{summary.name}</span>
        </button>
        <span className="shrink-0 text-xs text-muted-foreground">
          {t('visits.zoneCount', { n: summary.zone_count })}
        </span>
      </div>

      {expanded && (
        <div className="space-y-3 border-t px-3 py-3">
          {!detail && (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" /> {t('common.loading')}
            </p>
          )}
          {detail && (
            <>
              {canManageHere && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={attachedHere ? 'outline' : 'default'}
                    disabled={busy}
                    onClick={handleAttachToggle}
                  >
                    {attachedHere ? t('visits.detachFromThisBranch') : t('visits.attachToThisBranch')}
                  </Button>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="gap-1.5"
                      disabled={busy}
                      onClick={() => setEditMode(v => !v)}
                    >
                      <Pencil className="size-3.5" />
                      {editMode ? t('visits.viewMode') : t('visits.editMode')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="gap-1.5 text-destructive"
                      disabled={busy}
                      onClick={handleDeleteTemplate}
                    >
                      <Trash2 className="size-3.5" /> {t('visits.deleteTemplate')}
                    </Button>
                  </div>
                </div>
              )}

              {user?.is_superuser && (branchGroups ?? []).length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <div className="max-w-[16rem]">
                    <Select
                      value={selectedBranchGroup}
                      onChange={e => setSelectedBranchGroup(e.target.value)}
                      aria-label={t('visits.attachToBranchGroup')}
                      className="h-7 text-xs"
                    >
                      <option value="">{t('visits.attachToBranchGroup')}</option>
                      {(branchGroups ?? []).map((g: BranchGroupSummary) => (
                        <option key={g.id} value={g.id}>{g.name} ({g.zone_count})</option>
                      ))}
                    </Select>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy || !selectedBranchGroup}
                    onClick={handleAttachBranchGroup}
                  >
                    {t('visits.attachToBranchGroupAction')}
                  </Button>
                </div>
              )}

              {detail.zones.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">{t('visits.attachedBranches')}:</p>
                  {/* Chips instead of a flat comma-joined string — the plain-text
                      list read as opaque with no way to act on it (Alexey 09.09).
                      Managers get a remove (×) affordance per branch; everyone
                      else sees a plain read-only badge. */}
                  <div className="flex flex-wrap gap-1.5">
                    {detail.zones.map(z => (
                      <span
                        key={z.id}
                        className="inline-flex items-center gap-1 rounded-full border bg-muted/40 px-2 py-0.5 text-xs"
                      >
                        {z.name}
                        {canManageHere && (
                          <button
                            type="button"
                            className="text-muted-foreground hover:text-destructive"
                            disabled={busy}
                            aria-label={t('visits.detachFromThisBranch')}
                            onClick={() => handleDetachZone(z.id)}
                          >
                            ×
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2 border-t pt-2">
                {detail.groups.map(g => (
                  <GroupEditor
                    key={g.id}
                    templateId={detail.id}
                    group={g}
                    editMode={editMode}
                    onChanged={mutate}
                  />
                ))}
                {editMode && <AddGroupRow templateId={detail.id} onAdded={mutate} />}
              </div>

              {err && <p className="text-xs text-destructive">{err}</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Groups ───────────────────────────────────────────────────────────────

// Alexey 2026-08-25: "у шаблонов визитов хочется уметь задавать коэффициент
// как 1/3" — accepts "1/3", "-1/3", or a plain decimal like "0.5"/"-1".
function parseCoefficient(raw: string): number | null {
  const trimmed = raw.trim();
  const fraction = trimmed.match(/^(-?\d+(?:\.\d+)?)\s*\/\s*(-?\d+(?:\.\d+)?)$/);
  if (fraction) {
    const denominator = parseFloat(fraction[2]);
    if (denominator === 0) return null;
    return parseFloat(fraction[1]) / denominator;
  }
  const parsed = parseFloat(trimmed);
  return Number.isNaN(parsed) ? null : parsed;
}

// Round for display so "1/3" doesn't come back as "0.3333333333333333".
function formatCoefficient(value: number): string {
  return String(Math.round(value * 10000) / 10000);
}

function GroupEditor({
  templateId, group, editMode, onChanged,
}: {
  templateId: number;
  group: VisitParameterGroupDto;
  editMode: boolean;
  onChanged: () => void;
}) {
  const { t } = useT();
  const [name, setName] = useState(group.name);
  const [coefficient, setCoefficient] = useState(formatCoefficient(group.formula_coefficient));
  const [busy, setBusy] = useState(false);

  async function commitName() {
    if (name.trim() === group.name || !name.trim()) {
      setName(group.name);
      return;
    }
    setBusy(true);
    try {
      await patchVisitGroup(templateId, group.id, { name: name.trim() });
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function commitCoefficient() {
    const parsed = parseCoefficient(coefficient);
    if (parsed === null || parsed === group.formula_coefficient) {
      setCoefficient(formatCoefficient(group.formula_coefficient));
      return;
    }
    setBusy(true);
    try {
      // Not clamped — a negative coefficient (e.g. "Вредные привычки" = -1)
      // is a deliberate, supported case.
      await patchVisitGroup(templateId, group.id, { formula_coefficient: parsed });
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(t('visits.deleteGroupConfirm', { name: group.name }))) return;
    setBusy(true);
    try {
      await deleteVisitGroup(templateId, group.id);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-md bg-muted/20 p-2">
      <div className="flex flex-wrap items-center gap-2">
        {editMode ? (
          <Input
            value={name}
            onChange={e => setName(e.target.value)}
            onBlur={commitName}
            disabled={busy}
            className="h-7 max-w-[16rem] flex-1 text-xs font-medium"
          />
        ) : (
          <span className="flex-1 text-xs font-semibold">{group.name}</span>
        )}
        <Label className="text-[11px] text-muted-foreground">{t('visits.formulaCoefficient')}</Label>
        {editMode ? (
          <Input
            type="text"
            inputMode="text"
            placeholder="1/3"
            value={coefficient}
            onChange={e => setCoefficient(e.target.value)}
            onBlur={commitCoefficient}
            disabled={busy}
            className="h-7 w-20 text-xs"
          />
        ) : (
          <span className="text-xs tabular-nums">{formatCoefficient(group.formula_coefficient)}</span>
        )}
        {editMode && (
          <Button variant="ghost" size="icon-sm" disabled={busy} onClick={handleDelete} aria-label={t('visits.deleteGroup')}>
            <Trash2 className="size-3 text-destructive" />
          </Button>
        )}
      </div>

      <div className="mt-1.5 space-y-1 pl-2">
        {group.parameters.map(p => (
          <ParameterEditor key={p.id} templateId={templateId} groupId={group.id} parameter={p} editMode={editMode} onChanged={onChanged} />
        ))}
        {editMode && <AddParameterRow templateId={templateId} groupId={group.id} onAdded={onChanged} />}
      </div>
    </div>
  );
}

function AddGroupRow({ templateId, onAdded }: { templateId: number; onAdded: () => void }) {
  const { t } = useT();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  async function handleAdd() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await createVisitGroup(templateId, { name: name.trim() });
      setName('');
      onAdded();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Input
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder={t('visits.newGroupPlaceholder')}
        disabled={busy}
        className="h-7 max-w-[16rem] flex-1 text-xs"
      />
      <Button type="button" size="sm" variant="outline" className="gap-1.5" disabled={!name.trim() || busy} onClick={handleAdd}>
        <Plus className="size-3.5" /> {t('visits.addGroup')}
      </Button>
    </div>
  );
}

// ── Parameters ───────────────────────────────────────────────────────────

function ParameterEditor({
  templateId, groupId, parameter, editMode, onChanged,
}: {
  templateId: number;
  groupId: number;
  parameter: VisitParameterDto;
  editMode: boolean;
  onChanged: () => void;
}) {
  const { t } = useT();
  const [name, setName] = useState(parameter.name);
  const [weightYes, setWeightYes] = useState(String(parameter.weight_yes));
  const [weightNo, setWeightNo] = useState(String(parameter.weight_no));
  const [busy, setBusy] = useState(false);

  async function commit(field: 'name' | 'weight_yes' | 'weight_no', raw: string) {
    if (field === 'name') {
      if (!raw.trim() || raw.trim() === parameter.name) { setName(parameter.name); return; }
      setBusy(true);
      try { await patchVisitParameter(templateId, groupId, parameter.id, { name: raw.trim() }); onChanged(); }
      finally { setBusy(false); }
      return;
    }
    const parsed = parseFloat(raw);
    const current = field === 'weight_yes' ? parameter.weight_yes : parameter.weight_no;
    if (Number.isNaN(parsed) || parsed === current) {
      if (field === 'weight_yes') setWeightYes(String(current)); else setWeightNo(String(current));
      return;
    }
    setBusy(true);
    try {
      // Not clamped — negative weights are a deliberate, supported case
      // (e.g. "Курят в зале": да = -10).
      await patchVisitParameter(templateId, groupId, parameter.id, { [field]: parsed });
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(t('visits.deleteParameterConfirm', { name: parameter.name }))) return;
    setBusy(true);
    try {
      await deleteVisitParameter(templateId, groupId, parameter.id);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  if (!editMode) {
    return (
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="min-w-0 flex-1 truncate">{parameter.name}</span>
        <span className="tabular-nums text-muted-foreground">
          {t('visits.yes')}: {parameter.weight_yes} · {t('visits.no')}: {parameter.weight_no}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Input
        value={name}
        onChange={e => setName(e.target.value)}
        onBlur={() => commit('name', name)}
        disabled={busy}
        className="h-6 max-w-[14rem] flex-1 text-xs"
      />
      <Label className="text-[10px] text-muted-foreground">{t('visits.yes')}</Label>
      <Input
        type="number"
        step="any"
        value={weightYes}
        onChange={e => setWeightYes(e.target.value)}
        onBlur={() => commit('weight_yes', weightYes)}
        disabled={busy}
        className="h-6 w-16 text-xs"
      />
      <Label className="text-[10px] text-muted-foreground">{t('visits.no')}</Label>
      <Input
        type="number"
        step="any"
        value={weightNo}
        onChange={e => setWeightNo(e.target.value)}
        onBlur={() => commit('weight_no', weightNo)}
        disabled={busy}
        className="h-6 w-16 text-xs"
      />
      <Button variant="ghost" size="icon-sm" disabled={busy} onClick={handleDelete} aria-label={t('visits.deleteParameter')}>
        <Trash2 className="size-3 text-destructive" />
      </Button>
    </div>
  );
}

function AddParameterRow({ templateId, groupId, onAdded }: { templateId: number; groupId: number; onAdded: () => void }) {
  const { t } = useT();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  async function handleAdd() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await createVisitParameter(templateId, groupId, { name: name.trim() });
      setName('');
      onAdded();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Input
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder={t('visits.newParameterPlaceholder')}
        disabled={busy}
        className="h-6 max-w-[14rem] flex-1 text-xs"
      />
      <Button type="button" size="sm" variant="ghost" className="gap-1 text-xs" disabled={!name.trim() || busy} onClick={handleAdd}>
        <Plus className="size-3" /> {t('visits.addParameter')}
      </Button>
    </div>
  );
}
